// Shared Gantt/timeline date-axis helpers (Task 5.3).
//
// 📌 THIS DOCBLOCK IS THE CANONICAL HISTORY. The root `CLAUDE.md` Shared Modules
// bullet was a COPY of the corrected account below and is, since 2026-09-11, a
// one-line pointer here. Nothing was lost in the move — every claim it carried
// is stated below already, which is the reason it was the copy that went.
//
// 🚨 **CORRECTED 2026-08-08 — THIS HEADER WAS WRONG IN THREE WAYS, AND CLAUDE.md
// HAD COPIED IT VERBATIM. Two documents agreeing with each other is not
// evidence.** It read: *"extracted from the four Gantt views: TaskGanttView,
// LabourGanttView, CrossTeamGanttView, GagTimelineView. NEVER redefine these
// locally"*.
//
// 1. **`TaskGanttView` DOES NOT EXIST.** No such file anywhere under
//    `client/src`. This comment was the only place in the repo that named it.
// 2. **There are SIX Gantt-ish views, not four.** The two the old list omitted —
//    `components/budget/CrewGanttView.js` and `components/hires/GanttView.js` —
//    are precisely the two that import NOTHING from here.
//    (`components/gags/GagGanttView.js` is the sixth and needs no dates: it
//    wraps `CrossTeamGanttView`.)
// 3. **"NEVER redefine these locally" is false for three of the five real
//    callers**, and has been for as long as it has been written here:
//      GagTimelineView    — imports everything, incl. buildDateHeaders  ✅
//      CrossTeamGanttView — imports everything, incl. buildDateHeaders  ✅
//      LabourGanttView    — imports everything, incl. buildDateHeaders  ✅
//                           (since 2026-10-13, owner: the Labour view should
//                           have the Timeline's grid and date layout; its
//                           hand-rolled month band + week-only day row went)
//      CrewGanttView      — PRIVATE parseDate/addDays/daysDiff ❌
//      hires/GanttView    — PRIVATE parseDate/addDays/daysDiff ❌
//                           (both import `buildDateHeaders` since 2026-10-13:
//                           their private header builders were identical to
//                           it. Their date maths stays private, see below.)
//    🔀 **`toDateStr` came OFF both of those lists on 2026-09-08** — the two
//    private copies are deleted and both files now import the canonical
//    `utils/dateFormat.js#toLocalISO`, aliased to the local name. The remaining
//    three are the ones the 🪤 below is about, and they stay.
//
// 🪤 **OWED WORK, AND IT IS NOT A FIND-AND-REPLACE — this detail is the whole
// reason.** The three `parseDate` implementations DISAGREE, and the shared one
// is not strictly the best:
//   - this one          — null-guards the input AND `isNaN`-guards the result
//   - CrewGanttView:89  — `str.slice(0, 10)` first, so it ACCEPTS an ISO
//                         datetime that this one turns into `Invalid Date` →
//                         null. But it has no `isNaN` guard.
//   - hires/GanttView:107 — neither the slice nor the guard. A third variant.
// So swapping either private copy for this one is a live regression: any date
// arriving as an ISO datetime currently renders and would become `null` — **a
// bar silently disappearing.** A correct unification needs a helper with BOTH
// properties (tolerate a datetime AND guard NaN), which is a design step, not a
// substitution. Product owner scoped this *"A for now"* on 2026-08-08: only the
// header concern is in play; the date-helper reconciliation stays on the list.
//
// ⚠️ `str.slice(0, 10)` there is slicing an INPUT string, not a
// `.toISOString()` output — it is NOT the `no-utc-date-drift` forbidden
// pattern, though it looks like one, and that scanner reads comments as code.

// Parse a bare YYYY-MM-DD string to a local-midnight Date. Null-safe: returns
// null for falsy input (the guard TaskGanttView's old local copy was missing).
export const parseDate = (str) => {
  if (!str) return null;
  const d = new Date(str + 'T00:00:00');
  return isNaN(d) ? null : d;
};

// Whole days between two Dates (b - a). Rounds so DST shifts don't off-by-one.
export const daysDiff = (a, b) => Math.round((b - a) / 86400000);

// New Date n days after d (n may be negative).
export const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

// Format a Date back to bare YYYY-MM-DD (local, no timezone conversion).
//
// 🔀 **2026-09-08 — THIS IS NOW A RE-EXPORT, NOT AN IMPLEMENTATION.** It was one
// of FIVE hand-rolled copies of the same three lines under THREE names
// (`toDateStr` here and in `CrewGanttView`/`hires/GanttView`, `toDateLocal`
// inside `BudgetCostCodes`, `toLocalISO` in `utils/dateFormat.js`), declared as
// two LEGACY_DEBT groups (`a9681237`, `7cd60176`) in
// `scripts/duplication-manifest.js`. Root `CLAUDE.md` names `toLocalISO` as the
// canonical one, so all five now resolve to that single body.
//
// 🔑 **THE NAME SURVIVES DELIBERATELY, THE BODY DOES NOT.** Two modules import
// `toDateStr` from here (`teams/CrossTeamGanttView.js`, `gags/GagTimelineView.js`)
// alongside `parseDate` / `addDays` / `daysDiff`, and it reads as that vocabulary.
// One implementation, two names, is the `constants/statuses.js` → `STATUS_LABELS`
// barrel precedent — NOT a second definition.
//
// ⚠️ **THE ONE BEHAVIOURAL DIFFERENCE, MEASURED BEFORE THE SWAP:** `toLocalISO`
// carries `if (!d) return '';`, which this copy did not — a falsy argument used
// to THROW and now yields `''`. Every call site of all four copies was
// enumerated 2026-09-08 and every argument is a `Date` object (an `addDays`
// result, a `new Date(...)`, or a loop cursor), so the guard is unreachable and
// no behaviour moves. An `Invalid Date` still formats `NaN-NaN-NaN` on both.
//
// 🚫 **This does NOT license unifying `parseDate` / `addDays` / `daysDiff`** —
// see the header above; those three genuinely disagree and a naive swap makes
// bars disappear. Only the FORMATTER, which takes a `Date` and returns a string,
// was safe to unify.
export { toLocalISO as toDateStr } from './dateFormat';

// Snap a Date back to the Monday of its week (no-op if already Monday).
export const toMonday = (d) => {
  const r = new Date(d);
  r.setDate(r.getDate() - ((r.getDay() + 6) % 7));
  return r;
};

// Week-commencing label for a Monday Date: "w/c Mon 3 Aug" (year appended
// when not the current year). Single source for the breakdown table's
// shoot-week grouping AND the My Tasks gag-task shoot-week grouping — never
// redefine this format locally.
export const weekCommencingLabel = (monday) => {
  const day = monday.getDate();
  const month = monday.toLocaleDateString('en-GB', { month: 'short' });
  const year = monday.getFullYear();
  const suffix = year === new Date().getFullYear() ? '' : ` ${year}`;
  return `w/c Mon ${day} ${month}${suffix}`;
};

// Zoom level → pixels per day. Shared by every Gantt/timeline surface so the
// three zoom buttons feel identical everywhere.
export const DAY_WIDTHS = { week: 40, month: 16, quarter: 5 };

// Build the date-axis header cells for a zoom level.
// Returns [{ label, sublabel, offset (days from timelineStart), width (days),
// isWeekend }] — week zoom is one cell per day, month zoom one per week,
// quarter zoom one per calendar month.
export function buildDateHeaders(zoom, timelineStart, totalDays) {
  const headers = [];
  if (zoom === 'week') {
    for (let i = 0; i <= totalDays; i++) {
      const current = addDays(timelineStart, i);
      const dayOfWeek = current.getDay();
      headers.push({
        label: current.getDate().toString(),
        sublabel: i === 0 || current.getDate() === 1 ? current.toLocaleDateString('en-GB', { month: 'short' }) : '',
        offset: i, width: 1,
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      });
    }
  } else if (zoom === 'month') {
    let i = 0;
    while (i <= totalDays) {
      const current = addDays(timelineStart, i);
      const weekEnd = Math.min(i + 7, totalDays);
      headers.push({
        label: `${current.getDate()} ${current.toLocaleDateString('en-GB', { month: 'short' })}`,
        sublabel: '', offset: i, width: weekEnd - i, isWeekend: false,
      });
      i += 7;
    }
  } else {
    let i = 0;
    while (i <= totalDays) {
      const current = addDays(timelineStart, i);
      const nextMonth = new Date(current); nextMonth.setMonth(nextMonth.getMonth() + 1); nextMonth.setDate(1);
      const daysInSegment = Math.min(daysDiff(current, nextMonth), totalDays - i);
      headers.push({
        label: current.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
        sublabel: '', offset: i, width: Math.max(daysInSegment, 1), isWeekend: false,
      });
      i += Math.max(daysInSegment, 1);
    }
  }
  return headers;
}
